import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/logo.png";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/privacy", label: "Privacy" },
  { to: "/terms", label: "Terms" },
  { to: "/support", label: "Support" },
  { to: "/faq", label: "FAQ" },
];

// Pages that have a dark hero banner background
const darkHeroPages = ["/", "/privacy", "/terms", "/support", "/faq", "/contact"];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const hasDarkHero = darkHeroPages.includes(location.pathname);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleNavigation = (e, to) => {
    e.preventDefault();
    if (location.pathname === to) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.location.href = to;
    }
  };

  // When the page doesn't have a dark hero OR user has scrolled, show solid bg with dark text
  const isSolid = scrolled || !hasDarkHero;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isSolid
          ? "bg-white/80 backdrop-blur-2xl shadow-soft border-b border-gray-100/50"
          : "bg-transparent"
        }`}
    >
      <nav className="container mx-auto px-6 py-3 flex justify-between items-center">
        <Link
          to="/"
          className="flex items-center gap-2.5 group"
          onClick={(e) => handleNavigation(e, "/")}
        >
          <img
            src={logo}
            alt="ClockIn Master Logo"
            className="w-9 h-9 transition-transform duration-300 group-hover:scale-110"
          />
          <span className={`text-xl font-bold tracking-tight transition-colors duration-300 ${isSolid ? "text-primary" : "text-white"
            }`}>
            ClockIn Master
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={(e) => handleNavigation(e, link.to)}
              className={`relative px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${location.pathname === link.to
                  ? isSolid
                    ? "text-secondary bg-secondary/10"
                    : "text-white bg-white/15"
                  : isSolid
                    ? "text-gray-600 hover:text-primary hover:bg-gray-50"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
              aria-label={link.label}
            >
              {link.label}
            </Link>
          ))}
          <div className="flex items-center gap-2 ml-4">
            <Link
              to="/register"
              onClick={(e) => handleNavigation(e, "/register")}
              className="btn btn-secondary text-sm px-5 py-2.5 rounded-xl shadow-glow"
              aria-label="Get Started"
            >
              Get Started
            </Link>
            <a
              href="https://clockinmaster.com/auth/login"
              className={`px-5 py-2.5 rounded-xl text-sm font-medium border-2 transition-all duration-300 ${isSolid
                  ? "border-primary/20 text-primary hover:border-primary hover:bg-primary/5"
                  : "border-white/30 text-white hover:border-white hover:bg-white/10"
                }`}
              aria-label="Login"
            >
              Login
            </a>
          </div>
        </div>

        {/* Mobile menu button */}
        <button
          className={`md:hidden flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 ${isSolid ? "hover:bg-gray-100 text-primary" : "hover:bg-white/10 text-white"
            }`}
          onClick={() => setMenuOpen((m) => !m)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <span className="sr-only">Open main menu</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16M4 16h16" />
            )}
          </svg>
        </button>

        {/* Mobile dropdown */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="absolute top-full left-4 right-4 mt-2 bg-white rounded-2xl shadow-card-hover border border-gray-100 overflow-hidden z-50"
            >
              <div className="p-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={(e) => {
                      handleNavigation(e, link.to);
                      setMenuOpen(false);
                    }}
                    className={`block px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${location.pathname === link.to
                        ? "bg-secondary/10 text-secondary"
                        : "text-gray-700 hover:bg-gray-50 hover:text-primary"
                      }`}
                    aria-label={link.label}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              <div className="border-t border-gray-100 p-3 flex flex-col gap-2">
                <Link
                  to="/register"
                  onClick={(e) => {
                    handleNavigation(e, "/register");
                    setMenuOpen(false);
                  }}
                  className="btn btn-secondary text-sm w-full rounded-xl"
                  aria-label="Get Started"
                >
                  Get Started
                </Link>
                <a
                  href="https://clockinmaster.com/auth/login"
                  className="btn btn-outline text-sm w-full rounded-xl"
                  aria-label="Login"
                >
                  Login
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;